import { useState } from 'react'
import { listRoles, deleteRole, type Role } from '../api'
import { useTabs, useDataVersion } from '../layout/TabsContext'
import { useAuth } from '../layout/AuthContext'
import { useToast } from '../layout/ToastContext'
import { useServerList } from '../hooks/useServerList'
import { useColumnVisibility } from '../hooks/useColumnVisibility'
import { fill, textOf, MODULE_LABELS, CAP_SHORT } from '../../shared/texts'
import ListToolbar from '../components/ListToolbar'
import ListPanel from '../components/ListPanel'
import Pagination from '../components/Pagination'
import DataTable, { type Column } from '../components/DataTable'
import ColumnManager from '../components/ColumnManager'
import ConfirmDialog from '../components/ConfirmDialog'
import { DEFAULT_TABLE_PAGE_SIZE } from '../../shared/constants'
import { PAGES_ROLES_LIST_TEXTS as T } from '../../shared/texts'

const EditIcon = () => <img src="/icons/edit.svg" alt="" aria-hidden="true" />
const TrashIcon = () => <img src="/icons/delete.svg" alt="" aria-hidden="true" />

// Compact summary of a role's capabilities, e.g. "Kunden: RW · Rechnungen: R".
function capsSummary(caps: Record<string, string[]>) {
    const parts = Object.entries(caps)
        .filter(([, cs]) => cs?.length)
        .map(([m, cs]) => `${textOf(MODULE_LABELS, m)}: ${cs.map((c) => textOf(CAP_SHORT, c)).join('')}`)
    return parts.length ? parts.join(' · ') : '—'
}

export default function RolesList() {
    const { openTab, publishChange } = useTabs()
    const { toast } = useToast()
    const version = useDataVersion('roles')
    const { preferences, savePreferences, can } = useAuth()
    const canWrite = can('roles', 'write')
    const canDelete = can('roles', 'delete')
    const list = useServerList<Role>(listRoles, version, {
        sort: 'label', dir: 'asc', pageSize: preferences.tablePageSize?.roles ?? DEFAULT_TABLE_PAGE_SIZE,
    })
    const cols = useColumnVisibility('roles')

    const onPageSize = (n: number) => {
        list.setPageSize(n)
        savePreferences({ tablePageSize: { ...(preferences.tablePageSize ?? {}), roles: n } })
    }

    const openNew = () => openTab({ id: 'role-new', name: T.newRoleTab, component: 'role-editor' })
    const openEdit = (r: Role) => openTab({ id: `role-${r._id}`, name: fill(T.roleTab, { label: r.label }), component: 'role-editor', config: { id: r._id } })

    const [toDelete, setToDelete] = useState<Role | null>(null)
    const [delBusy, setDelBusy] = useState(false)
    const [delError, setDelError] = useState('')
    const askDelete = (r: Role) => { setDelError(''); setToDelete(r) }
    const cancelDelete = () => { if (!delBusy) { setToDelete(null); setDelError('') } }
    async function confirmDelete() {
        if (!toDelete) return
        setDelBusy(true); setDelError('')
        try {
            await deleteRole(toDelete._id)
            publishChange('roles')
            toast(T.deleted)
            setToDelete(null)
        } catch (e: any) {
            setDelError(e?.response?.data?.error ?? T.deleteFailed)
        } finally { setDelBusy(false) }
    }

    const columns: Column<Role>[] = [
        {
            key: 'label', header: T.columns.role, sortField: 'label', defaultWidth: 160,
            render: (r) => <>{r.label}{r.isSystem && <span className="badge" style={{ marginLeft: 8 }}>{T.systemBadge}</span>}</>,
        },
        { key: 'key', header: T.columns.key, sortField: 'key', defaultWidth: 120, render: (r) => <code>{r.key}</code> },
        { key: 'caps', header: T.columns.rights, defaultWidth: 280, render: (r) => r.isSystem ? T.fullAccess : capsSummary(r.capabilities) },
        { key: 'userCount', header: T.columns.users, className: 'center', defaultWidth: 80, render: (r) => r.userCount ?? 0 },
        {
            key: 'actions', header: T.columns.actions, defaultWidth: 100, sticky: true,
            render: (r) => (
                <div className="row-actions">
                    <button className="icon-action" title={r.isSystem ? T.systemNotEditable : canWrite ? T.edit : T.view}
                        aria-label={canWrite ? T.edit : T.view} disabled={r.isSystem} onClick={() => openEdit(r)}><EditIcon /></button>
                    <button className="icon-action" title={r.isSystem ? T.systemNotDeletable : T.delete}
                        aria-label={T.delete} disabled={r.isSystem || (r.userCount ?? 0) > 0 || !canDelete} onClick={() => askDelete(r)}><TrashIcon /></button>
                </div>
            ),
        },
    ]

    return (
        <div className="list-page">
            <ListToolbar title={T.title} infoText={T.info}
                action={canWrite ? <button onClick={openNew}>{T.newRole}</button> : undefined} />

            {list.error && <div className="notice err">{list.error}</div>}

            <ListPanel
                count={list.total}
                grandTotal={list.grandTotal}
                filtered={list.q.trim() !== ''}
                search={{ value: list.q, onChange: list.setQ }}
                columnManager={<ColumnManager columns={columns} hidden={cols.hidden} onToggle={cols.toggle} onReset={cols.reset} />}
            >
                {list.loading ? <p className="muted">{T.loading}</p> : list.rows.length === 0 ? (
                    <p className="muted">{T.noneFound}</p>
                ) : (
                    <DataTable key={cols.resetKey} tableId="roles" columns={columns} hidden={cols.hidden}
                        rows={list.rows} rowKey={(r) => r._id} sort={list.sort} dir={list.dir} onSort={list.toggleSort}
                        onRowActivate={(r) => { if (!r.isSystem) openEdit(r) }} />
                )}
            </ListPanel>

            <Pagination page={list.page} pageCount={list.pageCount} onPage={list.setPage} pageSize={list.pageSize} onPageSize={onPageSize} />

            <ConfirmDialog
                open={!!toDelete}
                title={T.deleteTitle}
                message={toDelete ? <>{fill(T.deleteQuestion, { label: toDelete.label })}</> : null}
                confirmLabel={T.delete}
                danger
                busy={delBusy}
                error={delError}
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
            />
        </div>
    )
}
